import { useMemo } from "react";

export const useDocumentationAnalysis = (vehicle) => {
  return useMemo(() => {
    if (!vehicle) return null;

    const analyzeDocuments = (documents, type = "vehicle") => {
      if (!documents || documents.length === 0) {
        return { status: "unknown", details: null };
      }

      const today = new Date();
      const issues = {
        expired: [],
        critical: [],
        warning: [],
      };

      documents.forEach((doc) => {
        if (!doc.expiration_date || !doc.is_required_for_operation) return;

        const expDate = new Date(doc.expiration_date);
        const daysUntil = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));

        if (daysUntil < 0) {
          issues.expired.push({
            name: doc.document_type_name,
            daysOverdue: Math.abs(daysUntil),
          });
        } else if (daysUntil <= 15) {
          issues.critical.push({
            name: doc.document_type_name,
            daysUntil,
          });
        } else if (daysUntil <= 30) {
          issues.warning.push({
            name: doc.document_type_name,
            daysUntil,
          });
        }
      });

      // Determinar estado general
      if (issues.expired.length > 0) {
        return {
          status: "expired",
          blocksActivation: true,
          details: {
            type,
            documents: issues.expired.map(d => d.name),
            daysUntil: null,
            message: `${issues.expired.length} documento(s) vencido(s)`,
          },
        };
      }

      if (issues.critical.length > 0) {
        return {
          status: "critical",
          blocksActivation: false,
          details: {
            type,
            documents: issues.critical.map(d => d.name),
            daysUntil: Math.min(...issues.critical.map(d => d.daysUntil)),
            message: `${issues.critical.length} documento(s) próximo(s) a vencer`,
          },
        };
      }

      if (issues.warning.length > 0) {
        return {
          status: "warning",
          blocksActivation: false,
          details: {
            type,
            documents: issues.warning.map(d => d.name),
            daysUntil: Math.min(...issues.warning.map(d => d.daysUntil)),
            message: `${issues.warning.length} documento(s) en advertencia`,
          },
        };
      }

      return {
        status: "valid",
        blocksActivation: false,
        details: {
          type,
          message: "Documentos al día",
        },
      };
    };

    // Analizar documentos del vehículo
    const vehicleAnalysis = analyzeDocuments(
      vehicle.vehicleDocuments || vehicle.documents, 
      "vehicle"
    );

    // Analizar documentos del operador
    const operatorAnalysis = analyzeDocuments(
      vehicle.operatorDocuments, 
      "operator"
    );

    // Analizar documentos del remolque (si aplica)
    const trailerAnalysis = vehicle.trailerDocuments 
      ? analyzeDocuments(vehicle.trailerDocuments, "trailer")
      : { status: "valid", blocksActivation: false };

    // Determinar estado general más crítico
    const allAnalyses = [vehicleAnalysis, operatorAnalysis, trailerAnalysis];
    const statePriority = { expired: 4, critical: 3, warning: 2, valid: 1, unknown: 0 };
    
    const criticalAnalysis = allAnalyses.reduce((prev, current) => 
      statePriority[current.status] > statePriority[prev.status] ? current : prev
    );

    // Combinar detalles de todos los análisis
    const combinedDetails = {
      vehicle: vehicleAnalysis,
      operator: operatorAnalysis,
      trailer: trailerAnalysis,
      overall: criticalAnalysis,
      blocksActivation: allAnalyses.some(a => a.blocksActivation),
    };

    return {
      status: criticalAnalysis.status,
      blocksActivation: combinedDetails.blocksActivation,
      details: combinedDetails,
      canBeScheduled: !combinedDetails.blocksActivation,
      warningMessage: criticalAnalysis.status === "critical" 
        ? criticalAnalysis.details?.message 
        : null,
    };
  }, [vehicle]);
};